import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './navigation.css';

const Navigation: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const isActive = (path: string) => location.pathname === path;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <nav className={`nav ${isScrolled ? 'nav--scrolled' : ''}`}>
        <div className="nav__container">
          
          {/* Logo */}
          <Link to="/" className="nav__logo" onClick={scrollToTop}>
            <img src="/logo/logo.png" alt="S-CUBUS" className="nav__logo-img" />
          </Link>

          {/* Desktop Menu */}
          <div className="nav__menu">
            <Link 
              to="/" 
              className={`nav__link ${isActive('/') ? 'nav__link--active' : ''}`}
              onClick={scrollToTop}
            >
              Home
            </Link>
            <Link 
              to="/why-unique" 
              className={`nav__link ${isActive('/why-unique') ? 'nav__link--active' : ''}`}
              onClick={scrollToTop}
            >
              Why S-CUBUS
            </Link>
            <Link 
              to="/about" 
              className={`nav__link ${isActive('/about') ? 'nav__link--active' : ''}`}
              onClick={scrollToTop}
            >
              About Us
            </Link>
            <Link 
              to="/philosophy" 
              className={`nav__link ${isActive('/philosophy') ? 'nav__link--active' : ''}`}
              onClick={scrollToTop}
            >
              Founders
            </Link>
            <Link 
              to="/courses" 
              className={`nav__link ${isActive('/courses') ? 'nav__link--active' : ''}`}
              onClick={scrollToTop}
            >
              Courses
            </Link>
            <a 
              href="https://sathii.scubus.com" 
              className="nav__link nav__link--sathi"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/logo/Sathii-Logo-Final-cc.png" alt="" className="nav__sathi-icon" />
              Sathi
            </a>
            <Link 
              to="/our-students" 
              className={`nav__link ${isActive('/our-students') ? 'nav__link--active' : ''}`}
              onClick={scrollToTop}
            >
              Our Students
            </Link>
            <Link 
              to="/contact" 
              className={`nav__link ${isActive('/contact') ? 'nav__link--active' : ''}`}
              onClick={scrollToTop}
            >
              Contact
            </Link>
          </div>

          {/* Right Actions */}
          <div className="nav__actions">
            <button 
              type="button"
              className="nav__launch-btn"
              onClick={() => {
                if ((window as any).openCourseLaunchModal) {
                  (window as any).openCourseLaunchModal();
                }
              }}
              aria-label="View course launch offers"
            >
              New Batches
            </button>
            <Link to="/dost" className="nav__btn" onClick={scrollToTop}>
              Login
            </Link>
            <a 
              href="https://wa.me/918796101095?text=Hi%20S-CUBUS%2C%20I%20want%20to%20know%20more%20about%20your%20courses."
              className="nav__call-btn"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Call us"
            >
              <svg className="nav__call-icon" viewBox="0 0 24 24" fill="none">
                <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="nav__call-text">Call</span>
            </a>
            <button 
              className="nav__hamburger"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>

        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="nav__overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile Menu */}
      <div className={`nav__mobile ${mobileOpen ? 'nav__mobile--open' : ''}`}>
        <div className="nav__mobile-header">
          <Link to="/" className="nav__logo" onClick={() => { scrollToTop(); setMobileOpen(false); }}>
            <img src="/logo/logo.png" alt="S-CUBUS" className="nav__logo-img" />
          </Link>
          <button 
            className="nav__mobile-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        <div className="nav__mobile-links">
          <Link 
            to="/" 
            className={`nav__mobile-link ${isActive('/') ? 'nav__mobile-link--active' : ''}`}
            onClick={() => { scrollToTop(); setMobileOpen(false); }}
          >
            Home
          </Link>
          <Link 
            to="/why-unique" 
            className={`nav__mobile-link ${isActive('/why-unique') ? 'nav__mobile-link--active' : ''}`}
            onClick={() => { scrollToTop(); setMobileOpen(false); }}
          >
            Why S-CUBUS
          </Link>
          <Link 
            to="/about" 
            className={`nav__mobile-link ${isActive('/about') ? 'nav__mobile-link--active' : ''}`}
            onClick={() => { scrollToTop(); setMobileOpen(false); }}
          >
            About Us
          </Link>
          <Link 
            to="/philosophy" 
            className={`nav__mobile-link ${isActive('/philosophy') ? 'nav__mobile-link--active' : ''}`}
            onClick={() => { scrollToTop(); setMobileOpen(false); }}
          >
            Founders
          </Link>
          <Link 
            to="/courses" 
            className={`nav__mobile-link ${isActive('/courses') ? 'nav__mobile-link--active' : ''}`}
            onClick={() => { scrollToTop(); setMobileOpen(false); }}
          >
            Courses
          </Link>
          <a 
            href="https://sathii.scubus.com" 
            className="nav__mobile-link nav__mobile-link--sathi"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
          >
            <img src="/logo/Sathii-Logo-Final-cc.png" alt="" className="nav__sathi-icon" />
            Sathi
          </a>
          <Link 
            to="/our-students" 
            className={`nav__mobile-link ${isActive('/our-students') ? 'nav__mobile-link--active' : ''}`}
            onClick={() => { scrollToTop(); setMobileOpen(false); }}
          >
            Our Students
          </Link>
          <Link 
            to="/contact" 
            className={`nav__mobile-link ${isActive('/contact') ? 'nav__mobile-link--active' : ''}`}
            onClick={() => { scrollToTop(); setMobileOpen(false); }}
          >
            Contact
          </Link>
        </div>

        <div className="nav__mobile-footer">
          <button 
            type="button"
            className="nav__mobile-launch-btn"
            onClick={() => {
              if ((window as any).openCourseLaunchModal) {
                (window as any).openCourseLaunchModal();
              }
              setMobileOpen(false);
            }}
          >
            New Batches
          </button>
          <Link to="/dost" className="nav__mobile-btn" onClick={() => { scrollToTop(); setMobileOpen(false); }}>
            Student Login
          </Link>
          <a 
            href="https://wa.me/918796101095?text=Hi%20S-CUBUS%2C%20I%20want%20to%20know%20more%20about%20your%20courses."
            className="nav__mobile-phone"
            target="_blank"
            rel="noopener noreferrer"
          >
            📞 +91 8796101095
          </a>
        </div>
      </div>
    </>
  );
};

export default Navigation;
